import { useState, useEffect, useCallback, useRef } from "react";
import API from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";
import { LockKey, Plus, Trash, UserCircle, At } from "@phosphor-icons/react";

const _ADMIN_ROLES = ["super_admin", "admin", "project_manager"];

const MENTION_RE = /@([\w.-]+)/g;

function renderWithMentions(text) {
  const parts = [];
  let lastIdx = 0;
  let m;
  MENTION_RE.lastIndex = 0;
  while ((m = MENTION_RE.exec(text)) !== null) {
    if (m.index > lastIdx) parts.push(text.slice(lastIdx, m.index));
    parts.push(
      <span key={`mention-${m.index}`} className="inline-flex items-center rounded px-1 py-0.5 text-[11px] font-semibold bg-amber-200/60 text-amber-900 dark:bg-amber-500/20 dark:text-amber-300">
        @{m[1]}
      </span>
    );
    lastIdx = m.index + m[0].length;
  }
  if (lastIdx < text.length) parts.push(text.slice(lastIdx));
  return parts;
}

/**
 * Private internal notes thread — admin-only, never shown to vendors.
 * Each admin can add to the thread; notes show author + timestamp.
 * Notes can be deleted only by their author or super_admin.
 */
export default function PrivateNotes({ entityType, entityId, className = "" }) {
  const { user } = useAuth();
  const isAdmin = _ADMIN_ROLES.includes(user?.role);

  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);

  // Mention autocomplete state
  const [mentionable, setMentionable] = useState([]);
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionQuery, setMentionQuery] = useState("");
  const [mentionStart, setMentionStart] = useState(-1);
  const [mentionIdx, setMentionIdx] = useState(0);
  const textareaRef = useRef(null);

  const fetchNotes = useCallback(async () => {
    if (!isAdmin || !entityId) return;
    setLoading(true);
    try {
      const { data } = await API.get(`/private-notes/${entityType}/${entityId}`);
      setNotes(data.notes || []);
    } catch (e) {
      if (e.response?.status !== 403) toast.error("Failed to load private notes");
    } finally { setLoading(false); }
  }, [entityType, entityId, isAdmin]);

  const fetchMentionable = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const { data } = await API.get("/private-notes/mentionable-users");
      setMentionable(data.users || []);
    } catch { /* silent */ }
  }, [isAdmin]);

  useEffect(() => { fetchNotes(); }, [fetchNotes]);
  useEffect(() => { fetchMentionable(); }, [fetchMentionable]);

  const onChangeText = (e) => {
    const val = e.target.value;
    setText(val);
    // Detect @mention trigger at cursor position
    const caret = e.target.selectionStart ?? val.length;
    const upto = val.slice(0, caret);
    const match = upto.match(/(^|\s)@([\w.-]*)$/);
    if (match) {
      setMentionOpen(true);
      setMentionQuery(match[2].toLowerCase());
      setMentionStart(caret - match[2].length - 1); // position of '@'
      setMentionIdx(0);
    } else {
      setMentionOpen(false);
    }
  };

  const filtered = mentionable
    .filter(u => {
      if (!mentionQuery) return true;
      const q = mentionQuery;
      return (
        (u.name || "").toLowerCase().includes(q) ||
        (u.handle || "").toLowerCase().includes(q) ||
        (u.email || "").toLowerCase().split("@")[0].includes(q)
      );
    })
    .slice(0, 6);

  const insertMention = (u) => {
    if (mentionStart < 0) return;
    const handle = u.handle || (u.name || "").replace(/\s+/g, ".");
    const before = text.slice(0, mentionStart);
    const after = text.slice((textareaRef.current?.selectionStart) ?? text.length);
    const next = `${before}@${handle} ${after}`;
    setText(next);
    setMentionOpen(false);
    // Restore focus + place caret after the inserted mention
    setTimeout(() => {
      textareaRef.current?.focus();
      const pos = (before + `@${handle} `).length;
      textareaRef.current?.setSelectionRange(pos, pos);
    }, 0);
  };

  const handleKeyDown = (e) => {
    if (!mentionOpen || filtered.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setMentionIdx(i => (i + 1) % filtered.length); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setMentionIdx(i => (i - 1 + filtered.length) % filtered.length); }
    else if (e.key === "Enter" || e.key === "Tab") { e.preventDefault(); insertMention(filtered[mentionIdx]); }
    else if (e.key === "Escape") { setMentionOpen(false); }
  };

  const addNote = async () => {
    const t = text.trim();
    if (!t) return;
    setSaving(true);
    try {
      const { data } = await API.post(`/private-notes/${entityType}/${entityId}`, { text: t });
      setNotes(prev => [data, ...prev]);
      setText("");
      const mentionedCount = (data.mentioned_users || []).length;
      toast.success(mentionedCount > 0 ? `Note added — ${mentionedCount} colleague${mentionedCount > 1 ? "s" : ""} notified` : "Note added");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add note");
    } finally { setSaving(false); }
  };

  const deleteNote = async (id) => {
    try {
      await API.delete(`/private-notes/${id}`);
      setNotes(prev => prev.filter(n => n.id !== id));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete");
    }
  };

  if (!isAdmin) return null;

  return (
    <div className={`border border-amber-200/50 dark:border-amber-900/30 rounded-md bg-amber-50/40 dark:bg-amber-950/20 ${className}`} data-testid={`private-notes-${entityType}`}>
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-amber-200/50 dark:border-amber-900/30">
        <LockKey size={14} weight="duotone" className="text-amber-600 dark:text-amber-400" />
        <h3 className="text-xs font-semibold uppercase tracking-wider text-amber-800 dark:text-amber-300">Private Notes</h3>
        <span className="text-[10px] text-amber-700/70 dark:text-amber-400/70 font-medium ml-auto">
          Admins only · hidden from suppliers & clients
        </span>
      </div>

      <div className="p-3 space-y-2 relative">
        <Textarea
          ref={textareaRef}
          placeholder="Add internal note. Type @ to mention a colleague (they'll be notified)."
          value={text}
          onChange={onChangeText}
          onKeyDown={handleKeyDown}
          rows={2}
          maxLength={5000}
          className="text-xs resize-none bg-background"
          data-testid="private-note-input"
        />

        {/* @mention autocomplete dropdown */}
        {mentionOpen && filtered.length > 0 && (
          <div className="absolute left-3 right-3 mt-1 z-20 rounded-md border border-border bg-popover shadow-lg overflow-hidden max-h-64 overflow-y-auto" data-testid="mention-dropdown">
            {filtered.map((u, i) => (
              <button
                key={u.id}
                type="button"
                onClick={() => insertMention(u)}
                onMouseEnter={() => setMentionIdx(i)}
                className={`w-full text-left flex items-center gap-2 px-2.5 py-2 text-xs transition-colors ${i === mentionIdx ? "bg-accent" : "hover:bg-accent/60"}`}
                data-testid={`mention-opt-${u.id}`}
              >
                <At size={12} className="text-amber-600 shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="font-semibold truncate">{u.name}</p>
                  <p className="text-[10px] text-muted-foreground truncate">{u.email}</p>
                </div>
                <span className="text-[9px] uppercase tracking-wider text-muted-foreground font-medium shrink-0">{(u.role || "").replace("_", " ")}</span>
              </button>
            ))}
          </div>
        )}

        <div className="flex items-center justify-between">
          <span className="text-[10px] text-muted-foreground">{text.length}/5000 · <span className="opacity-70">Type @ to mention</span></span>
          <Button size="sm" onClick={addNote} disabled={saving || !text.trim()} data-testid="private-note-add">
            {saving ? "Saving…" : <><Plus size={12} className="mr-1" /> Add note</>}
          </Button>
        </div>
      </div>

      <div className="divide-y divide-amber-200/40 dark:divide-amber-900/20 max-h-[360px] overflow-y-auto">
        {loading ? (
          <p className="px-4 py-4 text-xs text-muted-foreground text-center">Loading…</p>
        ) : notes.length === 0 ? (
          <p className="px-4 py-4 text-xs text-muted-foreground text-center italic">No private notes yet.</p>
        ) : (
          notes.map(n => {
            const canDelete = n.author_id === String(user?._id) || user?.role === "super_admin";
            return (
              <div key={n.id} className="px-4 py-3 group" data-testid={`private-note-${n.id}`}>
                <div className="flex items-center justify-between gap-2 mb-1">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <UserCircle size={14} weight="duotone" className="text-amber-700 dark:text-amber-400 shrink-0" />
                    <span className="text-xs font-semibold truncate">{n.author_name}</span>
                    {n.author_role && <span className="text-[9px] uppercase tracking-wider text-muted-foreground font-medium">{n.author_role.replace("_", " ")}</span>}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-[10px] text-muted-foreground whitespace-nowrap">
                      {new Date(n.created_at).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                    </span>
                    {canDelete && (
                      <button
                        onClick={() => deleteNote(n.id)}
                        className="opacity-0 group-hover:opacity-100 text-destructive transition-opacity"
                        title="Delete note"
                        data-testid={`private-note-delete-${n.id}`}
                      >
                        <Trash size={12} />
                      </button>
                    )}
                  </div>
                </div>
                <p className="text-xs text-foreground/80 whitespace-pre-wrap leading-relaxed">{n.text}</p>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
